export type BillingModel = "subscription" | "per_booking";

export function getBillingModel(business: { billing_model?: string | null } | null | undefined): BillingModel {
  const raw = business?.billing_model;
  return raw === "per_booking" ? "per_booking" : "subscription";
}

export function isSubscriptionBilling(business: { billing_model?: string | null } | null | undefined): boolean {
  return getBillingModel(business) === "subscription";
}

export function isPerBookingBilling(business: { billing_model?: string | null } | null | undefined): boolean {
  return getBillingModel(business) === "per_booking";
}

export function isPerBookingActivated(
  business: { signup_fee_paid_at?: string | null } | null | undefined,
): boolean {
  return !!(business as { signup_fee_paid_at?: string | null } | null | undefined)?.signup_fee_paid_at;
}

export function perBookingGraceEndsAt(business: {
  signup_grace_ends_at?: string | null;
  trial_ends_at?: string | null;
} | null | undefined): string | null {
  if (!business) return null;
  return business.signup_grace_ends_at || business.trial_ends_at || null;
}

/** Per-booking account locked until one-time signup fee is paid (after 7-day grace). */
export function isPerBookingSignupLocked(
  business: {
    billing_model?: string | null;
    signup_fee_paid_at?: string | null;
    signup_grace_ends_at?: string | null;
    trial_ends_at?: string | null;
  } | null | undefined,
  now = new Date(),
): boolean {
  if (!isPerBookingBilling(business)) return false;
  if (isPerBookingActivated(business)) return false;
  const ends = perBookingGraceEndsAt(business);
  if (!ends) return false;
  return new Date(ends).getTime() <= now.getTime();
}

export function perBookingGraceDaysRemaining(
  business: Parameters<typeof perBookingGraceEndsAt>[0],
  now = new Date(),
): number {
  const ends = perBookingGraceEndsAt(business);
  if (!ends) return 0;
  const ms = new Date(ends).getTime() - now.getTime();
  if (ms <= 0) return 0;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

export function needsPerBookingSignupFee(
  business: Parameters<typeof isPerBookingActivated>[0] & Parameters<typeof isPerBookingBilling>[0],
): boolean {
  return isPerBookingBilling(business) && !isPerBookingActivated(business);
}

/** Full app access (subscription active, or per-booking signup fee paid, or still in signup grace). */
export function hasFullAppAccess(
  business: Parameters<typeof isPerBookingSignupLocked>[0] & Parameters<typeof isPerBookingActivated>[0],
  subscriptionLocked: boolean,
): boolean {
  if (isPerBookingBilling(business)) {
    if (isPerBookingActivated(business)) return true;
    return !isPerBookingSignupLocked(business);
  }
  return !subscriptionLocked;
}

/** Buukd / Paystack checkout enabled (platform toggle or connected merchant Paystack). */
export function businessUsesOnlineCheckout(
  business: {
    collect_booking_deposits?: boolean | null;
    paystack_connected_account_enabled?: boolean | null;
  } | null | undefined,
): boolean {
  if (!business) return false;
  return (
    business.collect_booking_deposits === true ||
    business.paystack_connected_account_enabled === true
  );
}

/** Whether customers can pay online (booking deposit, invoice, etc.). */
export function businessAllowsCustomerPayments(
  business: Parameters<typeof isPerBookingSignupLocked>[0] & {
    subscription_status?: string | null;
  } | null | undefined,
  now = new Date(),
): boolean {
  if (!business) return false;
  if (isPerBookingBilling(business)) {
    if (isPerBookingActivated(business)) return true;
    const ends = perBookingGraceEndsAt(business);
    if (!ends) return false;
    return new Date(ends).getTime() > now.getTime();
  }
  return (business as { subscription_status?: string | null }).subscription_status !== "suspended";
}
